import {addBreadcrumb, captureMessage} from '@famly/vet_services_sentry';
import {EXPECTED_EXPIRATION_TIMESTAMP_KEY} from '@famly/vet_ts-utils_access-token';
import {getFromLocalStorage} from '@famly/stat_ts-utils_local-storage';

const BOOT_STAGES = [
    'entry-started',
    'translations-ready',
    'react-app-loaded',
    'access-token-resolved',
    'whitelabel-applied',
    'application-ready',
] as const;

type BootStage = (typeof BOOT_STAGES)[number];

// Nothing renders until boot finishes, so a step that hangs without throwing leaves a blank page and
// nothing in Sentry. Reporting a timeout makes those boots countable and names the stage that hung.
const BOOT_TIMEOUT_MS = 10_000;
// Email link scanners run the page with timers sped up, so the timeout fires within a second or two.
// A browser never fires a timer early; the slack only absorbs clocks coarsened against fingerprinting.
const MIN_ELAPSED_FOR_TIMEOUT_MS = BOOT_TIMEOUT_MS - 1_000;

const reachedStages: {stage: BootStage; detail?: string; atMs: number}[] = [];
let timeoutId: ReturnType<typeof setTimeout> | undefined;
let countdownStartedAtMs = 0;
let isDone = false;

const getAccessTokenExpiresInSeconds = () => {
    const expiresAt = getFromLocalStorage(EXPECTED_EXPIRATION_TIMESTAMP_KEY);
    const expiresAtMs = expiresAt ? new Date(expiresAt).getTime() : NaN;
    return Number.isNaN(expiresAtMs) ? null : Math.round((expiresAtMs - Date.now()) / 1000);
};

const reportTimeout = () => {
    isDone = true;
    document.removeEventListener('visibilitychange', onVisibilityChange);
    if (performance.now() - countdownStartedAtMs < MIN_ELAPSED_FOR_TIMEOUT_MS) {
        return;
    }
    // The gates resolve independently inside a zip, so the last stage reached need not sit before the one that hung.
    const firstMissingStage = BOOT_STAGES.find(stage => !reachedStages.some(reached => reached.stage === stage));

    captureMessage('app_boot_timeout', {
        level: 'warning',
        fingerprint: ['app_boot_timeout', firstMissingStage ?? 'none'],
        tags: {firstMissingBootStage: firstMissingStage ?? 'none'},
        extra: {
            reachedStages,
            msSincePageLoad: Math.round(performance.now()),
            online: navigator.onLine,
            accessTokenExpiresInSeconds: getAccessTokenExpiresInSeconds(),
        },
    });
};

// Browsers throttle timers in hidden tabs, so the timeout needs 10s of continuous visible time.
const startCountdown = () => {
    clearTimeout(timeoutId);
    if (!isDone && document.visibilityState === 'visible') {
        countdownStartedAtMs = performance.now();
        timeoutId = setTimeout(reportTimeout, BOOT_TIMEOUT_MS);
    }
};

const onVisibilityChange = () => startCountdown();

export const recordBootStage = (stage: BootStage, detail?: string) => {
    if (isDone) {
        return;
    }

    if (reachedStages.length === 0) {
        document.addEventListener('visibilitychange', onVisibilityChange);
        startCountdown();
    }

    reachedStages.push({stage, detail, atMs: Math.round(performance.now())});
    addBreadcrumb({category: 'boot', message: detail ? `${stage}: ${detail}` : stage});

    if (stage === 'application-ready') {
        isDone = true;
        clearTimeout(timeoutId);
        document.removeEventListener('visibilitychange', onVisibilityChange);
    }
};
